/**
 * Reusable Share Buttons component.
 * Shows 3 share options: WhatsApp, X/Twitter, Copy to clipboard.
 * The parent provides a `text` (the message body) and an optional `label`.
 */
import React, { useState } from 'react';
import { Share2, MessageCircle, Twitter, Link2, Check } from 'lucide-react';
import { analytics } from '../../services/analytics';
import './ShareButtons.css';

const SITE_URL = 'https://www.quitameloporquemelogasto.com.mx';

export const buildShareText = (message) => `${message}\n\nCalcula tu número aquí 👉 ${SITE_URL}`;

const ShareButtons = ({ text, label = 'Comparte tu resultado', testIdPrefix = 'share' }) => {
  const [copied, setCopied] = useState(false);
  const fullText = buildShareText(text);
  const encoded = encodeURIComponent(fullText);
  const whatsappUrl = `https://wa.me/?text=${encoded}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encoded}`;

  const trackShare = (channel) => analytics.shareClick(channel, testIdPrefix);

  const handleCopy = () => {
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = fullText;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(fullText).catch(fallback);
    } else {
      fallback();
    }
    trackShare('copy_link');
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="comparator-share" data-testid={`${testIdPrefix}-buttons`}>
      <div className="share-label">
        <Share2 className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <div className="share-buttons">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackShare('whatsapp')}
          className="share-btn share-whatsapp"
          data-testid={`${testIdPrefix}-whatsapp`}
          aria-label="Compartir por WhatsApp"
        >
          <MessageCircle className="w-4 h-4" />
          <span>WhatsApp</span>
        </a>
        <a
          href={twitterUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackShare('twitter')}
          className="share-btn share-twitter"
          data-testid={`${testIdPrefix}-twitter`}
          aria-label="Compartir en X/Twitter"
        >
          <Twitter className="w-4 h-4" />
          <span>X / Twitter</span>
        </a>
        <button
          type="button"
          onClick={handleCopy}
          className={`share-btn share-copy ${copied ? 'share-copied' : ''}`}
          data-testid={`${testIdPrefix}-copy`}
          aria-label="Copiar texto al portapapeles"
        >
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
          <span>{copied ? '¡Copiado!' : 'Copiar'}</span>
        </button>
      </div>
    </div>
  );
};

export default ShareButtons;
